import { DAILY_GAMES, GAME_BY_ID } from '../shared/games';
import { SITE_BRAND, blogPath, gamePath, hasArchive } from '../shared/site';
import { GAME_CONTENT } from './index';
import './seo.css';

/**
 * The home page's crawlable content.
 *
 * The hub itself is a grid of image tiles whose labels only exist once React
 * has mounted, so without this the site's most important URL has no text on it
 * at all. It doubles as the internal-linking hub: every game page, archive and
 * the guides are one hop from here.
 */
export default function HomeSeoContent() {
  const featured = [...DAILY_GAMES, GAME_BY_ID.longshot].filter(Boolean);
  return (
    <section className="seo" aria-label={`About ${SITE_BRAND}`}>
      <h1 className="seo-h1">Free daily puzzles — a new set every day</h1>
      <p className="seo-intro">
        {SITE_BRAND} publishes a fresh set of word, logic and number puzzles every day. Everyone
        gets the same puzzles, you get one attempt at each, and there is nothing to sign up for.
      </p>

      <h2>Today's puzzles</h2>
      <ul className="seo-links">
        {featured.map((g) => {
          const content = GAME_CONTENT[g.id];
          return (
            <li key={g.id}>
              <a href={gamePath(g.id)}>{g.title}</a> — {content ? content.metaDescription : g.tagline}
            </li>
          );
        })}
        <li>
          <a href="/gauntlet">Quest Gauntlet</a> — four puzzles, one timer, one shot at it.
        </li>
      </ul>

      <h2>How it works</h2>
      <ol className="seo-steps">
        <li>Every puzzle resets once a day, so there is a definite end to the day's play.</li>
        <li>You get one attempt at each puzzle and a single hint to spend inside it.</li>
        <li>Progress is kept in your own browser — no account, no email, no subscription.</li>
        <li>
          Missed a day? Each game keeps an archive of past puzzles with hints and answers.
        </li>
      </ol>

      <h2>Guides and answers</h2>
      <ul className="seo-links">
        <li>
          <a href={blogPath()}>Puzzle strategy guides</a> — how to solve cryptograms, word ladders
          and grouping puzzles.
        </li>
        <li>
          <a href="/privacy">Privacy policy</a> — what the site stores, and what it does not.
        </li>
        {featured.filter((g) => hasArchive(g.id)).slice(0, 5).map((g) => (
          <li key={g.id}>
            <a href={`/archive/${g.id}`}>{g.title} archive</a> — past puzzles, hints and answers.
          </li>
        ))}
      </ul>
    </section>
  );
}
